// Brand placeholders (art-assets §3):
// - wordmark (1200 × 400): "ball.io" in Fredoka 700, each letter in the next palette colour with
//   an outline; the "." is a small boiling-outline ball;
// - favicon (512 × 512): a palette-coloured ball with a two-dot face;
// - touch icon (180 × 180, opaque): the favicon ball on a paper background;
// - social preview (1200 × 630, opaque): template only (the server leaves the og:image tags out
//   when the file is absent).
//
// The live menu draws its own DOM wordmark and favicon (ui/brand.ts) with the same colours: the
// "." ball and the favicon ball use the palette colour at the "." position.

import { scaleRgb } from '@ballio/shared';
import type { Canvas2D } from '../../render/canvas.js';
import { css } from '../../render/theme.js';
import {
  art,
  drawBall,
  fillWith,
  inkCirclePath,
  PAPER_LIGHT,
  paletteColor,
  PLACEHOLDER_FONT,
  strokeInk,
} from './draw.js';

export const WORDMARK_TEXT = 'ball.io';

/** The favicon ball's colour: the palette colour of the wordmark's "." (index 4). */
export const BRAND_BALL_COLOR = paletteColor(WORDMARK_TEXT.indexOf('.'));

/** Letter outline darkening (deeper than a cell rim, for legibility on light and dark menus). */
const LETTER_RIM = 0.62;

/** The wordmark placeholder (default 1200 × 400, about 4 % padding). */
export function drawWordmark(width = 1200, height = 400): Canvas2D | null {
  return art(width, height, (ctx, w, h) => {
    const pad = Math.round(Math.min(w, h) * 0.04);
    let px = Math.round(h * 0.72);
    const glyphs = Array.from(WORDMARK_TEXT);
    const measure = (size: number): number[] => {
      ctx.font = `700 ${size}px ${PLACEHOLDER_FONT}`;
      return glyphs.map((g) => (g === '.' ? size * 0.42 : ctx.measureText(g).width || size * 0.6));
    };
    let widths = measure(px);
    const gap = (): number => px * 0.02;
    let total = widths.reduce((a, b) => a + b, 0) + gap() * (glyphs.length - 1);
    const maxW = w - pad * 2;
    if (total > maxW) {
      px = Math.floor((px * maxW) / total);
      widths = measure(px);
      total = widths.reduce((a, b) => a + b, 0) + gap() * (glyphs.length - 1);
    }
    let x = (w - total) / 2;
    const baseY = h / 2 + px * 0.05;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    glyphs.forEach((g, i) => {
      const gw = widths[i] ?? 0;
      const color = paletteColor(i);
      if (g === '.') {
        const r = px * 0.16;
        // The "." is a small ball with a hand-drawn (boiling-style) outline.
        ctx.beginPath();
        inkCirclePath(ctx, x + gw / 2, baseY + px * 0.22, r, 4242, 0.05, 40);
        fillWith(ctx, color);
        strokeInk(ctx, Math.max(2, px * 0.05), scaleRgb(color, LETTER_RIM));
      } else {
        ctx.font = `700 ${px}px ${PLACEHOLDER_FONT}`;
        ctx.lineWidth = Math.max(2, px * 0.09);
        ctx.strokeStyle = css(scaleRgb(color, LETTER_RIM));
        ctx.strokeText(g, x, baseY);
        ctx.fillStyle = css(color);
        ctx.fillText(g, x, baseY);
      }
      x += gw + gap();
    });
  });
}

/** The favicon placeholder: a palette-coloured ball with a two-dot face (default 512 × 512). */
export function drawFavicon(width = 512, height = 512): Canvas2D | null {
  return art(width, height, (ctx, w, h) => {
    const r = Math.min(w, h) * 0.44;
    drawBall(ctx, w / 2, h / 2, r, BRAND_BALL_COLOR, { rim: 0.09, face: 'dots', seed: 512 });
  });
}

/** The touch icon placeholder: the favicon ball on paper, opaque (default 180 × 180). */
export function drawTouchIcon(width = 180, height = 180): Canvas2D | null {
  return art(width, height, (ctx, w, h) => {
    ctx.fillStyle = css(PAPER_LIGHT);
    ctx.fillRect(0, 0, w, h);
    const r = Math.min(w, h) * 0.36;
    drawBall(ctx, w / 2, h / 2, r, BRAND_BALL_COLOR, { rim: 0.09, face: 'dots', seed: 180 });
  });
}

/** Social preview template (opaque 1200 × 630): paper, the wordmark and a few balls. */
export function drawSocialTemplate(width = 1200, height = 630): Canvas2D | null {
  const mark = drawWordmark(Math.round(width * 0.8), Math.round(height * 0.42));
  return art(width, height, (ctx, w, h) => {
    ctx.fillStyle = css(PAPER_LIGHT);
    ctx.fillRect(0, 0, w, h);
    const balls: readonly [number, number, number, number][] = [
      [0.12, 0.2, 0.07, 0],
      [0.88, 0.25, 0.1, 6],
      [0.18, 0.82, 0.12, 9],
      [0.8, 0.8, 0.06, 14],
      [0.5, 0.88, 0.04, 20],
    ];
    for (const [bx, by, br, c] of balls)
      drawBall(ctx, bx * w, by * h, br * h, paletteColor(c), { seed: c });
    if (mark) ctx.drawImage(mark, (w - mark.width) / 2, h * 0.27);
  });
}
